import React, { useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  getColumnsWidthStyles,
  getSharedAccordionStyles,
} from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import Accordion from 'app/shared/components/common/Accordion';
import dayjs from 'dayjs';
import PropType from 'prop-types';

import { COMMON_DATETIME_FORMAT } from 'constants/date';

import { UnderwritingTestIds } from '../../../Underwriting.testIds';

import { getIsESigned } from './LoanBundleHistory.utils';
import LoanBundleHistoryTable from './loanBundleHistoryTable';
import ScheduledBundleHistoryTable from './scheduledBundleHistoryTable';

const LoanBundleHistoryContent = ({ loanBundleHistorySorted }) => {
  const theme = useTheme();
  const classes = useMemo(() => getSharedAccordionStyles(theme), [theme]);
  const columnsStyles = useMemo(() => getColumnsWidthStyles(theme, { columns: 3 }), [theme]);

  if (!loanBundleHistorySorted) {
    return null;
  }

  return loanBundleHistorySorted.map(
    ({
      formBundleId,
      formBundleSignatureRequestId,
      createdDate,
      signatureItems,
      formBundleName,
      status,
      isSignable,
      isScheduledDelivery,
      scheduledDeliveryDate,
      acknowledgedDate,
    }) => {
      // Mirrors DisclosurePackageItem: a package cancelled before its scheduled generation reports
      // Canceled, and its body must still show what was scheduled rather than an empty table.
      const isScheduled = !!isScheduledDelivery;
      const createdDateToShow = isScheduled ? null : createdDate;

      const accordionBottom = isScheduled ? (
        <ScheduledBundleHistoryTable
          formBundleId={formBundleId}
          generatedDate={scheduledDeliveryDate}
        />
      ) : (
        <LoanBundleHistoryTable
          createdDate={createdDate}
          formBundleName={formBundleName}
          formBundleSignatureRequestId={formBundleSignatureRequestId}
          isSignable={isSignable}
          signatureItems={signatureItems}
        />
      );

      const accordionTop = (
        <Box className="w-full" sx={classes.accordionTop}>
          <Box className="flex md:hidden flex-col" sx={columnsStyles.wrapper}>
            <Typography variant="regular14">Form Bundle</Typography>
            <Typography variant="regular14">Date sent</Typography>
            <Typography variant="regular14">E-Signed</Typography>
            <Typography variant="regular14">Acknowledged</Typography>
          </Box>

          <Box
            className="flex flex-col md:flex-row"
            data-testid={UnderwritingTestIds.getFormBundleHistoryHeaderId(
              formBundleSignatureRequestId,
            )}
            sx={columnsStyles.wrapper}
          >
            <Typography variant="regular14">{formBundleName}</Typography>
            <Typography variant="regular14">
              {createdDateToShow ? dayjs(createdDateToShow).format(COMMON_DATETIME_FORMAT) : 'N/A'}
            </Typography>
            <Typography variant="regular14">{getIsESigned({ status, isSignable })}</Typography>
            <Typography variant="regular14">
              {acknowledgedDate ? dayjs(acknowledgedDate).format(COMMON_DATETIME_FORMAT) : 'N/A'}
            </Typography>
          </Box>
        </Box>
      );

      return (
        <Box key={formBundleSignatureRequestId} sx={classes.accordionWrapper}>
          <Accordion
            unmountOnExit
            bottom={accordionBottom}
            className="shadow-none"
            defaultExpanded={false}
            id={formBundleSignatureRequestId}
            isWithDivider={false}
            sx={classes.accordion}
            top={accordionTop}
          />
        </Box>
      );
    },
  );
};

LoanBundleHistoryContent.propTypes = {
  loanBundleHistorySorted: PropType.array,
};

export default LoanBundleHistoryContent;
